import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  useDisclosure,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Listing } from 'src/api/v1-api';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import useRequest from 'src/utils/hooks/useRequest';

interface ListingSelfReportedSoldPriceModalProps {
  listing: Listing;
  setListing: (listing: Listing) => void;
}

const ListingSelfReportedSoldPriceModal = ({
  listing,
  setListing,
}: ListingSelfReportedSoldPriceModalProps) => {
  const [price, setPrice] = useState<number | string>(
    listing.variants[0]?.offered_price ?? listing.variants[0]?.regular_price ?? 0,
  );
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { marketplace } = useMarketplaceContext();
  const { api } = useRequest();
  const router = useRouter();
  const showToast = useSingleToast();

  const { followUpToken, subStatus } = router.query;

  useEffect(() => {
    if (
      router.isReady &&
      followUpToken &&
      (subStatus === 'self_reported_loma_attributed' ||
        subStatus === 'self_reported_non_loma_attributed')
    ) {
      onOpen();
    }
  }, [followUpToken, router.isReady, subStatus]);

  const handleSave = async () => {
    if (
      !price ||
      parseFloat(price as string) <= 0 ||
      !listing?.id ||
      !marketplace?.id ||
      !followUpToken
    ) {
      return;
    }

    try {
      const response = await api.marketplace.listings.setSoldPrice(
        listing.id,
        marketplace.id,
        { sold_price: price },
        followUpToken as string,
      );
      showToast({
        title: 'Sold price updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setListing(response as Listing);
      onClose();
    } catch (error) {
      showToast({
        title: 'There was an error updating the sold price. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            listingId: listing.id,
            marketplaceId: marketplace.id,
            price,
          },
        },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Self-Reported Sold Price</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <p>
              Thanks for reporting the sold price of your listing. This information helps us to
              improve our marketplace and provide better insights to our users.
            </p>
            <FormControl>
              <FormLabel>Sold Price</FormLabel>
              <InputGroup>
                <NumberInput
                  value={price}
                  onChange={(valueString) => {
                    const value = parseFloat(valueString);
                    setPrice(Number.isNaN(value) ? 0 : value);
                  }}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </InputGroup>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSave}
            isDisabled={parseFloat(price as string) <= 0}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ListingSelfReportedSoldPriceModal;
